import React, { useState, useEffect, useCallback } from 'react';
import { IconWarning } from '../../Icons/IconWarning';

interface Field {
  id: string;
  label: string;
  name: string;
  required: boolean;
  type: string;
}

interface DatePickerProps {
  // TODO: proper types
  // fields: {
  //   day: Field;
  //   month: Field;
  //   year: Field;
  //   defaultValue?: string;
  //   relatesTo?: { value: string };
  //   validations?: { condition: { type: string }; message: string }[];
  //   errorMessages?: { typeMismatch: string };
  // };
  fields: any;
  onChange: (value: string) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({ fields, onChange: onUpdateValue }) => {
  const [date, setDate] = useState({
    day: '',
    month: '',
    year: '',
  });

  const [dayFieldError, setDayFieldError] = useState({ error: false, message: '' });
  const [monthFieldError, setMonthFieldError] = useState({ error: false, message: '' });
  const [yearFieldError, setYearFieldError] = useState({ error: false, message: '' });

  const currentYear = new Date().getFullYear();
  const requiredErrorMessage =
    fields?.validations?.find(({ condition }: any) => condition.type === 'IsRequiredCondition')
      ?.message || '';

  const setValue = useCallback(
    (updatedDate: typeof date) => {
      onUpdateValue(`${updatedDate.year}-${updatedDate.month}-${updatedDate.day}`);
    },
    [onUpdateValue]
  );
  useEffect(() => {
    // Setting default values (if provided)
    if (fields.defaultValue) {
      const [year, month, day] = fields.defaultValue.split('-');
      setDate({ day, month, year });
      setValue({ day, month, year });
    }
  }, [fields.defaultValue, fields.day.id, setValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof typeof date) => {
    const value = e.target.value;
    setDate((prev) => {
      const updatedDate = { ...prev, [field]: value };
      setValue(updatedDate);
      return updatedDate;
    });
  };

  const checkError = (
    e: React.FormEvent<HTMLInputElement>,
    errorSetter: React.Dispatch<React.SetStateAction<{ error: boolean; message: string }>>
  ) => {
    const input = e.target as HTMLInputElement;

    if (input.validity.typeMismatch) {
      errorSetter({
        error: true,
        message: fields.errorMessages?.typeMismatch || '',
      });
    } else if (input.validity.valueMissing) {
      errorSetter({
        error: true,
        message: requiredErrorMessage,
      });
    } else if (input.validity.rangeUnderflow || input.validity.rangeOverflow) {
      errorSetter({
        error: true,
        message: input.validationMessage,
      });
    } else {
      errorSetter({ error: false, message: '' });
    }
  };

  const getLabelValue = (field: Field) => (field.required ? `${field.label}*` : field.label);

  return (
    <section className="date-picker">
      {/* Day Field */}
      <div
        className={`d-flex flex-column mb-3 form-element ${
          dayFieldError.error ? 'form-element--error' : ''
        }`}
      >
        <label className="form-element__label form-label mb-2" htmlFor={fields.day.id}>
          {getLabelValue(fields.day)}
        </label>
        <span className="input-box">
          <input
            id={fields.day.id}
            value={date.day}
            min={1}
            max={31}
            type="number"
            required={fields.day.required}
            className="field-text"
            onChange={(e) => handleInputChange(e, 'day')}
            onBlur={(e) => checkError(e, setDayFieldError)}
            onInvalid={(e) => checkError(e, setDayFieldError)}
          />
          {dayFieldError.error && (
            <span className="input-icon input-icon--right input-icon--error">
              <IconWarning />
            </span>
          )}
        </span>
        {dayFieldError.error && (
          <span className="form-element__error field-error-text text-error-400">
            {dayFieldError.message}
          </span>
        )}
      </div>

      {/* Month Field */}
      <div
        className={`d-flex flex-column mb-3 form-element ${
          monthFieldError.error ? 'form-element--error' : ''
        }`}
      >
        <label className="form-element__label form-label mb-2" htmlFor={fields.month.id}>
          {getLabelValue(fields.month)}
        </label>
        <span className="input-box">
          <input
            id={fields.month.id}
            value={date.month}
            min={1}
            max={12}
            type="number"
            required={fields.month.required}
            className="field-text"
            onChange={(e) => handleInputChange(e, 'month')}
            onBlur={(e) => checkError(e, setMonthFieldError)}
            onInvalid={(e) => checkError(e, setMonthFieldError)}
          />
          {monthFieldError.error && (
            <span className="input-icon input-icon--right input-icon--error">
              <IconWarning />
            </span>
          )}
        </span>
        {monthFieldError.error && (
          <span className="form-element__error field-error-text text-error-400">
            {monthFieldError.message}
          </span>
        )}
      </div>

      {/* Year Field */}
      <div
        className={`d-flex flex-column mb-3 form-element ${
          yearFieldError.error ? 'form-element--error' : ''
        }`}
      >
        <label className="form-element__label form-label mb-2" htmlFor={fields.year.id}>
          {getLabelValue(fields.year)}
        </label>
        <span className="input-box">
          <input
            id={fields.year.id}
            value={date.year}
            min={1900}
            max={currentYear}
            type="number"
            required={fields.year.required}
            className="field-text"
            onChange={(e) => handleInputChange(e, 'year')}
            onBlur={(e) => checkError(e, setYearFieldError)}
            onInvalid={(e) => checkError(e, setYearFieldError)}
          />
          {yearFieldError.error && (
            <span className="input-icon input-icon--right input-icon--error">
              <IconWarning />
            </span>
          )}
        </span>
        {yearFieldError.error && (
          <span className="form-element__error field-error-text text-error-400">
            {yearFieldError.message}
          </span>
        )}
      </div>
    </section>
  );
};

export default DatePicker;
